import React, {
  PropsWithChildren,
  ReactElement,
  Reducer,
  useEffect,
  useReducer,
  useState,
} from 'react';

const FALLBACK_PROMO_BAR_HEIGHT = 56;

interface PromoBarContextProviderProps {
  PromoBarContext: React.Context<PromoBarProviderProps>;
  PromoBarProvider: ({ children }: PropsWithChildren) => ReactElement;
}

interface PromoBarState {
  isPromoBarOpen?: boolean;
  isPromoBarFloating?: boolean;
  isPromoBarScrolled?: boolean;
  promoBarHeight?: number;
  headerHeight?: number;
  headerExpandedHeight?: number;
}

interface ContextActions {
  handlePromoBarOpen: (isOpen: boolean) => void;
  handlePromoBarFloating: (isFloating: boolean) => void;
  setPromoBarHeight: (height: number) => void;
  setLayoutConfiguration: (headerHeight: number, headerExpandedHeight: number) => void;
}

export type PromoBarProviderProps = PromoBarState & ContextActions;

interface PromoBarAction {
  type: ActionType;
  payload?: PromoBarState;
}

enum ActionType {
  handlePromoBarOpen = 'openPromoBar',
  handlePromoBarFloating = 'handlePromoBarFloating',
  setPromoBarHeight = 'setPromoBarHeight',
  setLayoutConfiguration = 'setLayoutConfiguration',
}

const initialState: PromoBarState = {
  isPromoBarOpen: true,
  isPromoBarFloating: true,
  isPromoBarScrolled: false,
  promoBarHeight: FALLBACK_PROMO_BAR_HEIGHT,
  headerHeight: 0,
  headerExpandedHeight: 0,
};

const promoBarReducer: Reducer<PromoBarState, PromoBarAction> = (
  state: PromoBarState,
  action: PromoBarAction,
) => {
  switch (action.type) {
    case ActionType.handlePromoBarOpen:
      return { ...state, isPromoBarOpen: action.payload?.isPromoBarOpen };
    case ActionType.handlePromoBarFloating:
      return { ...state, isPromoBarFloating: action.payload?.isPromoBarFloating };
    case ActionType.setPromoBarHeight:
      return { ...state, promoBarHeight: action.payload?.promoBarHeight };
    case ActionType.setLayoutConfiguration:
      return {
        ...state,
        headerHeight: action.payload?.headerHeight,
        headerExpandedHeight: action.payload?.headerExpandedHeight,
      };
    default:
      return state;
  }
};

const contextCreate = (): PromoBarContextProviderProps => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actions: any = {
    handlePromoBarOpen,
    handlePromoBarFloating,
    setPromoBarHeight,
    setLayoutConfiguration,
  };

  const PromoBarContext = React.createContext<PromoBarProviderProps>({
    ...initialState,
    ...actions,
  });

  const PromoBarProvider = ({ children }: PropsWithChildren): ReactElement => {
    const [state, dispatch] = useReducer(promoBarReducer, initialState);
    const [isPromoBarScrolled, setIsPromoBarScrolled] = useState(false);

    useEffect(() => {
      const handleScroll = () => {
        setIsPromoBarScrolled(window.scrollY > (state.promoBarHeight || FALLBACK_PROMO_BAR_HEIGHT));
      };

      window.addEventListener('scroll', handleScroll);

      return (): void => {
        window.removeEventListener('scroll', handleScroll);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <PromoBarContext.Provider
        value={{
          ...state,
          isPromoBarScrolled,
          handlePromoBarOpen: (isOpen: boolean) => handlePromoBarOpen(dispatch, isOpen),
          handlePromoBarFloating: (isFloating: boolean) =>
            handlePromoBarFloating(dispatch, isFloating),
          setPromoBarHeight: (height: number) => setPromoBarHeight(dispatch, height),
          setLayoutConfiguration: (headerHeight: number, headerExpandedHeight: number) =>
            setLayoutConfiguration(dispatch, headerHeight, headerExpandedHeight),
        }}
      >
        {children}
      </PromoBarContext.Provider>
    );
  };

  return { PromoBarContext, PromoBarProvider };
};

const handlePromoBarOpen = async (dispatch: (action: PromoBarAction) => void, isOpen: boolean) => {
  dispatch({
    type: ActionType.handlePromoBarOpen,
    payload: { isPromoBarOpen: isOpen },
  });
};

const handlePromoBarFloating = async (
  dispatch: (action: PromoBarAction) => void,
  isFloating: boolean,
) => {
  dispatch({
    type: ActionType.handlePromoBarFloating,
    payload: {
      isPromoBarFloating: isFloating,
    },
  });
};

const setPromoBarHeight = async (dispatch: (action: PromoBarAction) => void, height: number) => {
  dispatch({
    type: ActionType.setPromoBarHeight,
    payload: {
      promoBarHeight: height,
    },
  });
};

const setLayoutConfiguration = async (
  dispatch: (action: PromoBarAction) => void,
  headerHeight: number,
  headerExpandedHeight: number,
) => {
  dispatch({
    type: ActionType.setLayoutConfiguration,
    payload: {
      headerHeight,
      headerExpandedHeight,
    },
  });
};

export const { PromoBarContext, PromoBarProvider } = contextCreate();
